import React, { useEffect, useMemo, useState } from 'react';
import { LogIn, Users, RefreshCw } from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { firebaseFirestoreService } from '../../services/firebaseFirestoreService';
import { displayDate, todayLocal, monthLocal } from '../../utils/dates';

/**
 * HOW OFTEN EACH AGENT SIGNED IN, BY DAY AND BY MONTH.
 *
 * Asked for by Ashish: an administrator needs to see how many times an agent
 * logged in on a day and across a month.
 *
 * WHY IT ASKS FOR THE SIGN-INS ITSELF.
 *
 * This panel used to count `LOGIN` entries out of the activity log that is
 * already loaded, which cost nothing -- and showed zero sign-ins for
 * everybody.
 *
 * The activity log is deliberately capped at the newest 300 entries, because
 * nothing ever deletes one and reading it in full on every sign-in would be
 * the largest single charge in the app. But EVERY action writes to that same
 * log: imports, allocations, edits, repairs. One Excel import can add
 * several hundred rows in a morning, so the LOGIN entries were pushed out
 * past the 300 within a day. The counting below was always right; it was
 * looking in a bin that had already been filled with something else.
 *
 * So it now asks the database for sign-ins specifically. Nothing else can
 * crowd them out, and it keeps working as the office grows. It is fetched
 * once when the panel is opened -- about one read -- rather than subscribed,
 * because nobody needs a sign-in count to change while they are looking at
 * it. The Refresh button is there for when they do.
 *
 * No new collection and no new writes on sign-in: the LOGIN entries were
 * always being recorded, and still are.
 *
 * WHAT A COUNT HERE MEANS, HONESTLY. It is the number of SIGN-INS recorded,
 * not time spent working and not days present. An agent who signs in on
 * three devices registers three; one who stays signed in all week registers
 * one. The column headings say so, because a number that looks like
 * attendance and is not would be worse than no number at all.
 */
const AgentLoginCounts: React.FC = () => {
  const { users, currentUser } = useSRMS();
  const [month, setMonth] = useState<string>(() => monthLocal());
  const [query, setQuery] = useState('');

  /** The sign-in entries, fetched for this panel rather than taken from the
   *  general activity log. `null` means the first fetch has not finished. */
  const [loginLogs, setLoginLogs] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const isAdmin =
    currentUser.role === 'admin' || (currentUser.role as string) === 'super_admin';

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    firebaseFirestoreService
      .fetchLoginLogs()
      .then((rows) => {
        if (!cancelled) setLoginLogs(rows || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, reloadKey]);

  if (!isAdmin) return null;

  /** Every LOGIN entry, bucketed by user and by day. */
  const data = useMemo(() => {
    const logins = (loginLogs || []).filter((l: any) => l.actionType === 'LOGIN');

    const byUser = new Map<
      string,
      { name: string; role: string; days: Map<string, number>; total: number; last: string }
    >();

    logins.forEach((l: any) => {
      const key = String(l.userId || l.userName || '').trim().toLowerCase();
      if (!key) return;
      // The log stores a full timestamp; the DAY is its first ten characters
      // in local form. `displayDate` is only for showing it.
      const day = String(l.timestamp || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return;

      const row =
        byUser.get(key) ||
        { name: l.userName || key, role: l.role || '', days: new Map<string, number>(), total: 0, last: '' };
      row.days.set(day, (row.days.get(day) || 0) + 1);
      row.total += 1;
      if (!row.last || String(l.timestamp) > row.last) row.last = String(l.timestamp);
      if (l.userName) row.name = l.userName;
      byUser.set(key, row);
    });

    // Agents who have never signed in still belong on the list -- a missing
    // row reads as "no data", and a zero is the answer somebody is looking
    // for.
    (users || [])
      .filter((u: any) => u.role === 'agent')
      .forEach((u: any) => {
        const key = String(u.id || '').trim().toLowerCase();
        const alt = String(u.agentId || '').trim().toLowerCase();
        if (byUser.has(key) || byUser.has(alt)) return;
        byUser.set(key || alt || u.name, {
          name: u.name || u.agentId || u.id,
          role: 'agent',
          days: new Map(),
          total: 0,
          last: '',
        });
      });

    const today = todayLocal();
    const rows = Array.from(byUser.entries()).map(([key, r]) => {
      let inMonth = 0;
      let daysActive = 0;
      r.days.forEach((n, d) => {
        if (d.slice(0, 7) === month) {
          inMonth += n;
          daysActive += 1;
        }
      });
      return {
        key,
        name: r.name,
        role: r.role,
        today: r.days.get(today) || 0,
        inMonth,
        daysActive,
        lifetime: r.total,
        last: r.last,
      };
    });

    rows.sort((a, b) => b.inMonth - a.inMonth || a.name.localeCompare(b.name));
    return rows;
  }, [loginLogs, users, month]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter((r) => r.name.toLowerCase().includes(q) || r.key.includes(q));
  }, [data, query]);

  /** The months the log actually covers, newest first. */
  const months = useMemo(() => {
    const set = new Set<string>();
    (loginLogs || []).forEach((l: any) => {
      if (l.actionType !== 'LOGIN') return;
      const m = String(l.timestamp || '').slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(m)) set.add(m);
    });
    set.add(monthLocal());
    return Array.from(set).sort().reverse();
  }, [loginLogs]);

  const monthLabel = (m: string) => {
    const [y, mm] = m.split('-').map(Number);
    if (!y || !mm) return m;
    return new Date(y, mm - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const totalLogins = shown.reduce((s, r) => s + r.inMonth, 0);

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center gap-2">
        <LogIn className="w-4 h-4 text-blue-600" />
        <div>
          <span className="font-bold text-slate-900 block">Agent sign-ins</span>
          <span className="text-slate-500 text-[11px]">
            How many times each agent signed in, today and across the month. A sign-in is
            not a day worked: signing in on two devices counts twice, and staying signed
            in all week counts once.
          </span>
        </div>
      </div>

      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2.5 text-[11px]">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 font-semibold focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m)}
              </option>
            ))}
          </select>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find an agent…"
            aria-label="Find an agent"
            className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 min-w-0 flex-1 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading}
            className="px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5 shrink-0 cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>{loading ? 'Loading…' : 'Refresh'}</span>
          </button>
          <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
            <Users className="w-3.5 h-3.5" />
            {shown.length} agent(s) · {totalLogins.toLocaleString('en-IN')} sign-in(s) in{' '}
            {monthLabel(month)}
          </span>
        </div>

        {loginLogs === null ? (
          <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 font-semibold">
            Loading sign-ins…
          </div>
        ) : data.length === 0 ? (
          <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 font-bold">
            No sign-ins recorded yet.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="p-2">Agent</th>
                  <th className="p-2 text-right">Today</th>
                  <th className="p-2 text-right">This month</th>
                  <th className="p-2 text-right">Days signed in</th>
                  <th className="p-2 text-right">All time</th>
                  <th className="p-2">Last sign-in</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono">
                {shown.map((r) => (
                  <tr key={`li-${r.key}`} className="hover:bg-slate-50">
                    <td className="p-2">
                      <span className="font-sans font-bold text-slate-900">{r.name}</span>
                      {r.role && r.role !== 'agent' && (
                        <span className="font-sans text-slate-400 ml-1.5">({r.role})</span>
                      )}
                    </td>
                    <td className="p-2 text-right font-bold text-slate-900">{r.today}</td>
                    <td className="p-2 text-right font-bold text-blue-700">{r.inMonth}</td>
                    <td className="p-2 text-right text-slate-600">{r.daysActive}</td>
                    <td className="p-2 text-right text-slate-500">{r.lifetime}</td>
                    <td className="p-2 font-sans text-slate-500">
                      {r.last ? `${displayDate(r.last.slice(0, 10))} ${r.last.slice(11, 16)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-slate-400">
          This covers the activity log currently loaded. An agent with no sign-ins shows
          zero rather than being left off the list.
        </div>
      </div>
    </div>
  );
};

export default AgentLoginCounts;
